import { MunicipalityComparative } from '@/graphql/types/municipality';

export interface DataItem {
  subject: string;
  [key: string]: any;
}

export interface SeriesInfo {
  dataKey: string;
  name: string;
  fillColor: string;
  strokeColor: string;
}

function reorderDimensionsForRadar(
  data: MunicipalityComparative[],
  type: 'opportunity' | 'risk'
): MunicipalityComparative[] {
  const desiredOrder = [
    'gestao_uso_terra',
    'clima_natureza',
    'social',
    'institucional',
    type === 'risk' ? 'seguranca' : 'economica',
  ];

  const dimensionsMap = new Map<string, MunicipalityComparative[]>();
  data.forEach((item) => {
    const bigqueryId = item.dimension.bigquery_id || '';
    if (!dimensionsMap.has(bigqueryId)) {
      dimensionsMap.set(bigqueryId, []);
    }
    dimensionsMap.get(bigqueryId)?.push(item);
  });

  const reorderedData: MunicipalityComparative[] = [];
  desiredOrder.forEach((bigqueryId) => {
    const items = dimensionsMap.get(bigqueryId);
    if (items) {
      reorderedData.push(...items);
    }
  });

  data.forEach((item) => {
    const bigqueryId = item.dimension.bigquery_id || '';
    if (!desiredOrder.includes(bigqueryId)) {
      reorderedData.push(item);
    }
  });

  return reorderedData;
}

export function buildChartDataByMunicipality(
  data: MunicipalityComparative[],
  valueKey: 'municipality_opportunity' | 'municipality_risk'
): DataItem[] {
  const isRiskChart = valueKey === 'municipality_risk';
  const reorderedData = reorderDimensionsForRadar(data, isRiskChart ? 'risk' : 'opportunity');

  const municipalityMap = new Map<string, string>();
  let municipalityCounter = 1;
  const groupedByDimension = new Map<string, DataItem>();

  const classificationKey = isRiskChart ? 'classification_risk' : 'classification_opportunity';

  // Helper function to convert classification to numeric value
  const classificationToValue = (classification: string): number => {
    switch (classification) {
      case 'LOW':
        return 1;
      case 'MEDIUM_LOW':
        return 2;
      case 'MEDIUM_HIGH':
        return 3;
      case 'HIGH':
        return 4;
      default:
        return 1;
    }
  };

  reorderedData.forEach((item) => {
    const dimName = item.dimension.name;

    if (!municipalityMap.has(item.municipality.municipality_code)) {
      municipalityMap.set(item.municipality.municipality_code, `series${municipalityCounter++}`);
    }

    const seriesKey = municipalityMap.get(item.municipality.municipality_code);

    if (!groupedByDimension.has(dimName)) groupedByDimension.set(dimName, { subject: dimName });

    const row = groupedByDimension.get(dimName);
    if (row) {
      const classification = item[classificationKey];
      row[seriesKey as keyof DataItem] = classificationToValue(classification);
      row[`${seriesKey}_classification`] = classification;
    }
  });

  const result: DataItem[] = [];
  const addedDimensions = new Set<string>();

  reorderedData.forEach((item) => {
    const dimName = item.dimension.name;
    if (!addedDimensions.has(dimName)) {
      const dataItem = groupedByDimension.get(dimName);
      if (dataItem) {
        result.push(dataItem);
        addedDimensions.add(dimName);
      }
    }
  });

  return result;
}

export function buildSeriesList(
  data: MunicipalityComparative[],
  seriesColors: {
    name: string;
    fillColor: string;
    strokeColor: string;
    dataKey: string;
  }[]
): SeriesInfo[] {
  const seen = new Set<string>();
  let counter = 1;

  const seriesList: SeriesInfo[] = [];

  data.forEach((item) => {
    const name = item.municipality.name_municipality ?? '';
    if (!name || seen.has(name)) return;

    seen.add(name);
    const color = seriesColors.find((c) => c.name === name);

    seriesList.push({
      dataKey: `series${counter++}`,
      name,
      fillColor: color?.fillColor ?? '#0679FF4D',
      strokeColor: color?.strokeColor ?? '#1E99FF',
    });
  });

  return seriesList;
}
